import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { TaskDrawer } from '../components/TaskDrawer';
import { WORK_STAGE_LABELS, type WorkStage } from '../lib/types';
import { formatDate } from '../lib/dates';

interface TaskProgress {
  id: string;
  key: string;
  title: string;
  project: { id: string; name: string; code: string };
  stage: WorkStage | null;
  percent: number;
  status: { label: string; color: string; category: string } | null;
  assignees: string;
  dueDate: string | null;
  overdue: boolean;
  blocked: boolean;
  done: boolean;
}

/**
 * Every task in flight, and how far along it is.
 *
 * THE QUESTION THIS ANSWERS IS "WHERE IS MY THING". The by-project tab says what share of a
 * project is finished and what shape the rest is in; neither tells somebody where the one task
 * they care about has got to. This is that, a row at a time — and the customer reads it,
 * because the stage is theirs to know and ours to set.
 *
 * The percentage comes from the API rather than being worked out here, so the number in the
 * export, the number on the bar and the number the server would report are the same number.
 */
/**
 * The bar's colour says what KIND of progress it is, not how much — the width already says
 * that. Finished is the settled green; work in flight is the accent; a blocked task is not
 * progressing at all and says so rather than showing a cheerful bar at 60%.
 */
function barColour(task: TaskProgress): string {
  if (task.done) return 'var(--ok)';
  if (task.blocked) return 'var(--text-dim)';
  return 'var(--accent)';
}

interface Report {
  /** Over every task, closed included — the list below is open work only. */
  percentComplete: number;
  taskCount: number;
  tasks: TaskProgress[];
}

export function TaskProgressReport({ companyId }: { companyId: string }) {
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The task open over the report, if any — never a page this list is thrown away for. */
  const [open, setOpen] = useState<{ id: string; projectId: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setReport(await api.get<Report>(`/companies/${companyId}/task-progress`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not build the task report');
      setReport({ percentComplete: 0, taskCount: 0, tasks: [] });
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<TaskProgress>[] = useMemo(
    () => [
      {
        key: 'task',
        header: 'Task',
        width: '32%',
        value: (task) => `${task.key} ${task.title}`,
        filter: 'text',
        render: (task) => (
          <span className="row" style={{ gap: 8 }}>
            <span className="key">{task.key}</span>
            <span>{task.title}</span>
            {task.blocked && <span className="badge badge-accent">blocked</span>}
          </span>
        ),
      },
      {
        key: 'project',
        header: 'Project',
        value: (task) => task.project.name,
        filter: 'choice',
        render: (task) => (
          /* Stops the row's own click, so the project cell still goes to the project. */
          <Link
            to={`/projects/${task.project.id}`}
            onClick={(event) => event.stopPropagation()}
            className="muted"
          >
            {task.project.name}
          </Link>
        ),
      },
      {
        key: 'stage',
        header: 'Stage',
        /* The word, so the filter reads the way the column does. Unstaged says so. */
        value: (task) => (task.stage ? WORK_STAGE_LABELS[task.stage] : 'Not started'),
        filter: 'choice',
        render: (task) =>
          task.stage ? (
            <span className={`badge stage-${task.stage}`}>{WORK_STAGE_LABELS[task.stage]}</span>
          ) : (
            <span className="dim">Not started</span>
          ),
      },
      {
        key: 'percent',
        header: 'Done',
        width: '170px',
        value: (task) => task.percent,
        filter: 'none',
        render: (task) => (
          <span className="row" style={{ gap: 8 }}>
            {/* The same bar the by-project report uses, so a percentage reads the same everywhere. */}
            <span className="progress grow" style={{ display: 'block' }}>
              <span
                className="progress-fill"
                style={{ display: 'block', width: `${task.percent}%`, background: barColour(task) }}
              />
            </span>
            <span className="mono" style={{ minWidth: 34, textAlign: 'right' }}>
              {task.percent}%
            </span>
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        value: (task) => task.status?.label ?? '',
        filter: 'choice',
        render: (task) =>
          task.status ? (
            <span className="status-pill" style={{ background: task.status.color }}>
              {task.status.label}
            </span>
          ) : (
            <span className="dim">—</span>
          ),
      },
      {
        key: 'assignees',
        header: 'With',
        value: (task) => task.assignees,
        filter: 'text',
        render: (task) => <span className="muted">{task.assignees || <span className="dim">Unassigned</span>}</span>,
      },
      {
        key: 'due',
        header: 'Due',
        value: (task) => task.dueDate,
        filter: 'none',
        render: (task) => (
          <span className="mono" style={task.overdue ? { color: 'var(--accent)', fontWeight: 600 } : undefined}>
            {task.dueDate ? formatDate(task.dueDate) : <span className="dim">—</span>}
          </span>
        ),
      },
    ],
    [],
  );

  if (error) return <div className="callout">{error}</div>;
  if (!report) return <div className="empty">Building the report…</div>;

  const tasks = report.tasks;

  if (tasks.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <h3>Nothing in flight</h3>
          <p>Every task here is closed, or nothing has been raised yet.</p>
        </div>
      </div>
    );
  }

  const passed = tasks.filter((task) => task.percent === 100).length;
  const testing = tasks.filter((task) => task.stage === 'testing').length;

  return (
    <>
      <div className="stat-row mb-2">
        <div className="stat">
          <div className="stat-label">In flight</div>
          <div className="stat-value">{tasks.length}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Passed</div>
          <div className="stat-value">{passed}</div>
        </div>
        <div className="stat">
          <div className="stat-label">In testing</div>
          <div className="stat-value">{testing}</div>
        </div>
        {/*
          FROM THE API, NOT AVERAGED OVER THE LIST BELOW.
          
          This used to average the rows on screen, and the list deliberately leaves out closed
          work — so it read 52% while the project report read 55%, the difference being one
          closed task at 100%. It was also backwards: closing a finished task took a 100% out of
          the average and pushed it DOWN, which made tidying up look like going backwards.
          
          One number, one population, worked out once on the server.
        */}
        <div className="stat">
          <div className="stat-label">Average done</div>
          <div className="stat-value">{report.percentComplete}%</div>
          <div className="stat-label" style={{ fontWeight: 400 }}>
            over {report.taskCount} {report.taskCount === 1 ? 'task' : 'tasks'}, closed included
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-pad">
          {/*
            The row opens the task IN PLACE, the way Priorities and Issues do.
            A report is something you read down; being thrown onto a board to glance at one row
            loses your place in it — and here it would lose the filters and sort as well.
          */}
          <DataTable
            tableKey="task-progress"
            exportName="task-progress"
            columns={columns}
            rows={tasks}
            searchPlaceholder="Find a task"
            defaultSort={{ key: 'percent', direction: 'desc' }}
            onRowClick={(task) => setOpen({ id: task.id, projectId: task.project.id })}
          />
        </div>
      </div>

      {/* Editing a task can move its stage, so the percentages are rebuilt on the way out. */}
      {open && (
        <TaskDrawer
          itemId={open.id}
          projectId={open.projectId}
          onClose={() => setOpen(null)}
          onChanged={load}
        />
      )}
    </>
  );
}
